import React, { useState } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useGetTags, useCreateTag, useUpdateTag, useDeleteTag } from '../hooks/tags';
import { ITag } from '../Interfaces/ITag';
import TagChip from '../core/TagChip';
import { useAlert } from '../core/AlertContext';
import { primaryButtonSx, secondaryButtonSx, dangerButtonSx } from '../core/pvtaStyles';

const PRESET_COLORS = ['#1f4d2b', '#123d24', '#2f5d3a', '#c8a94d', '#6f5512', '#9f5622', '#1f6f8b', '#4f5b52'];

const TagsSettings: React.FC = () => {
    const { tags, isLoadingTags } = useGetTags();
    const { createTag, isCreatingTag } = useCreateTag();
    const { updateTag, isUpdatingTag } = useUpdateTag();
    const { deleteTag, isDeletingTag } = useDeleteTag();
    const { showAlert } = useAlert();

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<ITag | null>(null);
    const [name, setName] = useState('');
    const [color, setColor] = useState('#1f4d2b');
    const [deleteCandidate, setDeleteCandidate] = useState<ITag | null>(null);

    const openCreate = () => {
        setEditing(null);
        setName('');
        setColor('#1f4d2b');
        setDialogOpen(true);
    };

    const openEdit = (tag: ITag) => {
        setEditing(tag);
        setName(tag.name);
        setColor(tag.color);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            showAlert({ severity: 'error', text: 'Bitte einen Namen angeben.' });
            return;
        }
        try {
            if (editing) {
                await updateTag({ id: editing.id, name: trimmed, color });
                showAlert({ text: 'Tag aktualisiert.' });
            } else {
                await createTag({ name: trimmed, color });
                showAlert({ text: 'Tag erstellt.' });
            }
            setDialogOpen(false);
        } catch {
            showAlert({ severity: 'error', text: 'Speichern fehlgeschlagen. Ist der Name evtl. schon vergeben?' });
        }
    };

    const handleDelete = async () => {
        if (!deleteCandidate) {
            return;
        }
        try {
            await deleteTag(deleteCandidate.id);
            showAlert({ text: 'Tag gelöscht.' });
            setDeleteCandidate(null);
        } catch {
            showAlert({ severity: 'error', text: 'Löschen fehlgeschlagen.' });
        }
    };

    return (
        <Box>
            <Stack
                direction="row"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 2.5 }}
            >
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Tags / Schlagworte
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'var(--pvta-muted)', mt: 0.25 }}>
                        Werden an Blog-Beiträge und Veranstaltungen vergeben.
                    </Typography>
                </Box>
                <Button onClick={openCreate} startIcon={<AddIcon />} sx={{ ...primaryButtonSx, flexShrink: 0 }}>
                    Neuer Tag
                </Button>
            </Stack>

            {isLoadingTags ? (
                <Stack spacing={1.25}>
                    {[0, 1, 2].map((item) => (
                        <Skeleton key={item} variant="rounded" height={56} sx={{ borderRadius: 'var(--pvta-radius-sm)' }} />
                    ))}
                </Stack>
            ) : tags.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center', border: '1px dashed var(--pvta-border)', borderRadius: 'var(--pvta-radius-sm)' }}>
                    <Typography sx={{ color: 'var(--pvta-muted)' }}>Noch keine Tags angelegt.</Typography>
                </Box>
            ) : (
                <Stack spacing={1}>
                    {tags.map((tag) => (
                        <Stack
                            key={tag.id}
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            sx={{
                                p: 1.5,
                                borderRadius: 'var(--pvta-radius-sm)',
                                border: '1px solid var(--pvta-border)',
                                backgroundColor: 'var(--pvta-surface)',
                            }}
                        >
                            <TagChip tag={tag} />
                            <Typography sx={{ flex: 1, minWidth: 0, color: 'var(--pvta-muted)' }} noWrap>
                                {tag.color}
                            </Typography>
                            <Tooltip title="Bearbeiten">
                                <IconButton size="small" onClick={() => openEdit(tag)}>
                                    <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Löschen">
                                <IconButton size="small" onClick={() => setDeleteCandidate(tag)}>
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    ))}
                </Stack>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle sx={{ fontWeight: 700 }}>{editing ? 'Tag bearbeiten' : 'Neuer Tag'}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2.5} sx={{ mt: 1 }}>
                        <TextField
                            label="Name"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            fullWidth
                            autoFocus
                        />
                        <Box>
                            <Typography variant="caption" sx={{ color: 'var(--pvta-muted)', display: 'block', mb: 1 }}>
                                Farbe
                            </Typography>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                                <Box
                                    component="input"
                                    type="color"
                                    value={color}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => setColor(event.target.value)}
                                    sx={{
                                        width: 44,
                                        height: 44,
                                        p: 0,
                                        border: '1px solid var(--pvta-border)',
                                        borderRadius: 'var(--pvta-radius-sm)',
                                        background: 'none',
                                        cursor: 'pointer',
                                    }}
                                />
                                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                    {PRESET_COLORS.map((preset) => (
                                        <Box
                                            key={preset}
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => setColor(preset)}
                                            onKeyDown={(keyboardEvent) => {
                                                if (keyboardEvent.key === 'Enter' || keyboardEvent.key === ' ') {
                                                    keyboardEvent.preventDefault();
                                                    setColor(preset);
                                                }
                                            }}
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: '50%',
                                                backgroundColor: preset,
                                                cursor: 'pointer',
                                                border: color.toLowerCase() === preset.toLowerCase() ? '2px solid #1f2520' : '2px solid #ffffff',
                                                boxShadow: '0 0 0 1px rgba(20,35,25,0.15)',
                                            }}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" sx={{ color: 'var(--pvta-muted)', display: 'block', mb: 0.75 }}>
                                    Vorschau
                                </Typography>
                                <TagChip tag={{ id: editing?.id ?? 0, name: name.trim() || 'Beispiel', color }} />
                            </Box>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                    <Button
                        onClick={() => { void handleSave(); }}
                        disabled={isCreatingTag || isUpdatingTag}
                        sx={primaryButtonSx}
                    >
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteCandidate !== null} onClose={() => setDeleteCandidate(null)}>
                <DialogTitle>Tag löschen?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        Soll der Tag &quot;{deleteCandidate?.name}&quot; wirklich gelöscht werden? Er wird von allen Beiträgen und Veranstaltungen entfernt.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteCandidate(null)} sx={secondaryButtonSx}>Abbrechen</Button>
                    <Button sx={dangerButtonSx} disabled={isDeletingTag} onClick={() => { void handleDelete(); }}>
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TagsSettings;
